import { client } from "../utils/netlify";
import { WISHLIST_PRODUCT_DETAILS_QUERY } from "../gql/wishlistProductDetails";
import apolloClient from "../gql/apolloClient";
import { getVariantPrice } from "./pdpHelpers/priceHelpers";
import { generateBadge } from "./pdpHelpers/badgeHelpers";
import {
  MULTIPLE_PRODUCT_DETAILS_QUERY,
} from "gql/productDetails";
import { generalConstants } from "@/utils/constants";
import { reportError } from "./errorHelper";
import { isBeautyOrJewelryPDP } from "./generalHelper";

/**
 * Get the customer id from the full id string returned from shopify API
 * @param {string} productId - Full id string returned from shopify API
 */
export const getNumberId = (productId) => {
  const itemId = productId;
  const splitedItemId = itemId.split("/", -1);
  const id = splitedItemId[splitedItemId.length - 1];

  return id;
};

export const getCustomerId = (id) => {
  try{
    if (typeof window !== "undefined") {
      if(window.atob(window.btoa(id)) == id){ //not encoded
        return getNumberId(id)
      } 
      else {
        const base64DecodedId = window.atob(id);
        const customerId = getNumberId(base64DecodedId);
        return customerId;
      }
    } 
    else return null;
  }
  catch(error) {
    
    return null;
  }

};

/**
 * Create a new wishlist with unique Hash for a user or retrieve old one if it exists
 * @param {Object} request
 * @param {string} request.storeCustomerId - customerId of logged in customer (whose wishlist is to be retreived).
 */
export const createOrGetWishlist = async (request) => {
  try{
    let resp = await client("create-or-get-wishlist", {})
    return resp;
  }catch(error){
    reportError("Error in createOrGetWishlist");
    reportError(error)
  }
};

/**
 * Get wishlist with unique Hash for a user
 * @param {Object} request
 * @param {string} request.storeCustomerId - customerId of logged in customer (whose wishlist is to be retreived).
 */
export const getWishlistByCustomerId = async (request) => {
  try{
    let resp = await client("get-wishlist-by-customer-id", request)
    return resp;
  }catch(error){
    reportError("Error in getWishlistByCustomerId");
    reportError(error)
  }
};

/**
 * Retrieve the wishlisted items
 * @param {Object} request
 * @param {string} request.HashId - hash ID of wishlist.
 */
export async function getWishlistProducts(request) {
  try{
    let resp = await client("get-items-from-wishlist", request)
    // Get additional product details for wishlist items such as vendor name, selected color, size etc.
    const wishlistResponse = await getProductDetails(resp);
    return wishlistResponse;
  }catch(error){
    reportError("Error in getWishlistProducts");
    reportError(error)
  }
}

export const getProductDetails = async (wishlistItems) => {
  if (Array.isArray(wishlistItems)) {
    const variantIds = wishlistItems.map((item) => {
      return Buffer.from(
        `gid://shopify/ProductVariant/${item.ProductVariantId}`
      ).toString("base64");
    });

    const res = await apolloClient.query({
      query: WISHLIST_PRODUCT_DETAILS_QUERY,
      variables: {
        ids: variantIds,
      },
      fetchPolicy: "no-cache"
    });

    if (res?.data?.nodes) {
      const variants = res.data.nodes;
      const wishlistItemsWithAdditionalDetails = [];

      variants.forEach((variant, index) => {
        // If product variant is added to wishlist previously, and at a later point that product variant is removed from shopify, then "variant" here will be null. Hence need to check first if variant is defined.
        if (variant) {
          // added condition for beauty & jewelry products to get the color from base_color metafield
          const selectedColor = !isBeautyOrJewelryPDP(variant?.product?.tags)?variant?.baseColor?.value: variant.selectedOptions.find(
            (option) => option.name.toLowerCase() === "color"
          )?.value;

          const selectedSize = variant.selectedOptions.find(
            (option) => option.name.toLowerCase() === "size" || option.name.toLowerCase() === "denominations"
          )?.value;

          const selectedLetter = variant.selectedOptions.find(
            (option) => option.name.toLowerCase() === "style"
          )?.value;

          const { returnedPrice, returnedSalePrice } = getVariantPrice(variant);

          // fetch the badge_curated and availability tag from the product tags
          const badge_curated = generateBadge(variant.product.tags);  
          const availabilityType = variant.product?.tags?.includes(generalConstants.pre_order_tag) ?  generalConstants.pre_order : variant.product?.tags?.includes(generalConstants.available_now_tag) ? generalConstants.available_now : '';

          wishlistItemsWithAdditionalDetails.push({
            ...wishlistItems[index],
            Color: selectedColor,
            Size: selectedSize,
            Letter: selectedLetter,
            Vendor: variant.product.vendor,
            AvailableForSale: variant.availableForSale,
            FormattedPrice: returnedPrice,
            FormattedSalePrice: returnedSalePrice,
            badge_curated: badge_curated,
            availability: availabilityType,
            tags: variant.product.tags
          });
        }
      });
      return wishlistItemsWithAdditionalDetails;
    }
  }
  return [];
};

export const getProductDetailsByWishlist = async (wishlistItems) => {
  if (Array.isArray(wishlistItems)) {
    const ids = wishlistItems.map((item) => {
      return Buffer.from(`gid://shopify/Product/${item.ProductId}`).toString(
        "base64"
      );
    });
    const res = await apolloClient.query({
      query: MULTIPLE_PRODUCT_DETAILS_QUERY,
      variables: {
        ids: ids,
      },
      fetchPolicy: "no-cache",
    });

    return res.data.nodes;
  }
  return [];
};

/**
 * Add product to wishlist
 * @param {Object} request
 * @param {string} request.encodedCustomerID - encoded Customer ID
 * @param {string} request.hash - Hash ID of the wishlist
 * @param {Array} request.currentWishlistItems - Array of current wishlist items before adding new item
 * @param {string} request.FormattedPrice - Price of the product variant in string format with currency
 * @param {string} request.FormattedSalePrice - Sale price (red color price) of the product variant in string format with currency
 * @param {string} request.Vendor - Vendor (brand name) of the product
 * @param {string} request.AvailableForSale - Availability of the product variant
 * @param {string} request.Color - Color of the product variant
 * @param {string} request.Size - Size of the product variant
 */
export const addProductToWishlist = async (request) => {
  // Check if item already exists in wishlist
  const itemExistsInWishlist = request.currentWishlistItems.some((item) => {
    return item.ProductVariantId === request.ProductVariantId;
  });

  if (!itemExistsInWishlist) {
    const hash = request.hash;
    if (hash) {
      try{
        let resp = await client("add-to-wishlist", request)
        return resp;
      }catch(error){
        reportError("Error in addProductToWishlist");
        reportError(error)
      }
    } else {
      const resp = {
        error: "Wishlist hash not provided",
      };

      return resp;
    }
  } else {
    const resp = {
      error: "Item already exists in wishlist",
    };

    return resp;
  }
};

/**
 * Remove product from wishlist
 * @param {Object} request
 * @param {string} request.hash - Hash Id of the wishlist
 * @param {string} request.productVariantId - number id of product variant
 */
export const removeProductFromWishlist = async (request) => {
  const hash = request.hash;
  // Adding the hash to request object to be sent to netlify function
  if (hash) {
    try{
      let resp = await client("remove-from-wishlist", request)
      return resp;
    }catch(error){
      reportError("Error in removeProductFromWishlist");
      reportError(error)
    }
  } else {
    const resp = {
      error: "Wishlist hash not provided",
    };
    return resp;
  }
};

//  this function merge the user wishlist ( merge session )
export const mergeUsersWishlist = async (HashId,userSavedHashId,usersWishlistItems,timeout) => {
  try {
    //  fetch the wishlist items present in the cookie before login
    let request = timeout ? await getWishlistProducts({ HashId: HashId, timeout:timeout }) : await getWishlistProducts({ HashId: HashId }); // Fetch user's saved wishlist

    let responses = [];
    if (request !== null ) {
      for (const item of request) {
        let resObj = {
          hash: userSavedHashId,
          ProductId: item.ProductId,
          ProductVariantId: item.ProductVariantId,
          ProductName: item.ProductName,
          ProductImage: item.ProductImage,
          ProductLink: item.ProductLink,
          Price: item.Price,
          tag: item.tags,
          badge_curated: item.badge_curated,
          availability: item.availability,
          currentWishlistItems: usersWishlistItems
        };
        //  add an item to wishlist
        let response = await addProductToWishlist(resObj);
        responses.push(response);
      }
    }
    return responses;
  } catch (error) {
    reportError("Error in mergeUsersWishlist");
    reportError(error)
    throw error;
  }
 };
